import type {
  DemoContentSettingsOutput,
  TrainingVisitKind,
  TrainingVisitStatus,
} from '../../services/api/types.gen';

/**
 * What sample data a sandbox build seeds, as the Demo content tab edits it.
 *
 * Defaults mirror the backend's content.py, where every default is what a
 * build seeded before these settings existed.
 */

/** One visit on the training patient's chart. */
export type ResolvedVisit = {
  kind: TrainingVisitKind;
  /** Day of the 60-day episode; the SOC is day 0. */
  day: number;
  status: TrainingVisitStatus;
  /** In-progress OASIS visits: about how many scrubber errors are left. */
  scrubber_errors: number;
  /** In-progress skilled nursing visits: how much of the note is filled in. */
  filled_percent: number;
};

/**
 * The content a build seeds, as the dialog edits it.
 *
 * Only what is worth choosing. How many wounds, medications and goals the one
 * worked chart holds, which intake columns get a card, how many patients sit
 * in each claim state, how many coding charts and how big the reporting set
 * is are all fixed on the server: each had an obvious right answer, and a
 * sandbox built with fewer demos worse. See `content.py`.
 */
/** Whose data a build uses, per kind: the agency's own, or invented. */
export type ResolvedKinnserSources = {
  clinics: boolean;
  payers: boolean;
  physicians: boolean;
  staff: boolean;
};

export type ResolvedDemoContent = {
  training_patient: {
    enabled: boolean;
    /** The SOC first, always; the wounds, goals and orders hang off it. */
    visits: ResolvedVisit[];
  };
  intakes: { enabled: boolean };
  billing: { enabled: boolean };
  reporting: {
    enabled: boolean;
  };
  cahps: { enabled: boolean };
  coding: Record<string, never>;
  kinnser_sources: ResolvedKinnserSources;
};

// The board columns the intake demo covers, in board order (content.py).

// Upper bounds, the same as content.py's.
export const CONTENT_LIMITS = {
  socScrubberErrors: 60,
  wounds: 4,
  medications: 7,
  goals: 4,
  visits: 12,
  lastEpisodeDay: 59,
  // The build is this far into the episode; later visits can only be not started.
  todayEpisodeDay: 35,
  billingPerState: 5,
  codingCharts: 3,
};

export const VISIT_KIND_LABELS: Record<TrainingVisitKind, string> = {
  soc: 'SOC',
  recert: 'Recert',
  skilled_nursing: 'Skilled nursing',
};

export const VISIT_STATUS_LABELS: Record<TrainingVisitStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
};

export const FILL_STEPS = [25, 50, 75, 100];

export const isOasisVisit = (kind: TrainingVisitKind) =>
  kind !== 'skilled_nursing';

const visit = (
  kind: TrainingVisitKind,
  day: number,
  status: TrainingVisitStatus,
  scrubberErrors = 20
): ResolvedVisit => ({
  kind,
  day,
  status,
  scrubber_errors: scrubberErrors,
  filled_percent: 100,
});

/** A new row: a skilled nursing visit a week after the last one. */
export const newVisit = (visits: ResolvedVisit[]): ResolvedVisit => {
  const day = Math.min(
    CONTENT_LIMITS.lastEpisodeDay,
    Math.max(0, ...visits.map(row => row.day)) + 7
  );
  return visit(
    'skilled_nursing',
    day,
    day > CONTENT_LIMITS.todayEpisodeDay ? 'not_started' : 'in_progress'
  );
};

export const DEMO_CONTENT_DEFAULTS: ResolvedDemoContent = {
  training_patient: {
    enabled: true,
    // The chart every demo had: a signed SOC, four open nursing visits, and a
    // recert left open with ethnicity and race unanswered.
    visits: [
      visit('soc', 0, 'completed'),
      ...[3, 10, 18, 26].map(day =>
        visit('skilled_nursing', day, 'in_progress')
      ),
      visit('recert', 34, 'in_progress', 2),
    ],
  },
  intakes: { enabled: true },
  billing: { enabled: true },
  reporting: {
    enabled: true,
  },
  cahps: { enabled: true },
  coding: {},
  // Staff off, the rest on: seeding a sandbox with a prospect's real employee
  // names is a privacy decision, so it is made on purpose. Mirrors content.py.
  kinnser_sources: {
    clinics: true,
    payers: true,
    physicians: true,
    staff: false,
  },
};

/** Every field present: a missing section or field takes its default. */
export function resolveDemoContent(
  content: DemoContentSettingsOutput | undefined
): ResolvedDemoContent {
  const d = DEMO_CONTENT_DEFAULTS;
  return {
    training_patient: {
      ...d.training_patient,
      ...content?.training_patient,
      visits: (
        content?.training_patient?.visits ?? d.training_patient.visits
      ).map(row => ({
        ...visit(row.kind, row.day, row.status ?? 'in_progress'),
        ...row,
      })) as ResolvedVisit[],
    },
    intakes: { ...d.intakes, ...content?.intakes },
    billing: { ...d.billing, ...content?.billing },
    reporting: {
      ...d.reporting,
      ...content?.reporting,
    },
    cahps: { ...d.cahps, ...content?.cahps },
    coding: {},
    kinnser_sources: {
      ...d.kinnser_sources,
      ...content?.kinnser_sources,
    },
  };
}

/** What stops a save, or null. Bounds are enforced by the inputs themselves.
 *
 * Only the visits can be wrong now: everything else a section seeds is fixed,
 * so a section that is on always has something to build.
 */
export function demoContentError(content: ResolvedDemoContent): string | null {
  if (
    content.training_patient.enabled &&
    content.training_patient.visits.some(
      row =>
        row.day > CONTENT_LIMITS.todayEpisodeDay && row.status !== 'not_started'
    )
  )
    return `A visit after today (day ${CONTENT_LIMITS.todayEpisodeDay}) can only be not started.`;
  return null;
}
